<template>
  <div
    class="preselect__container"
    :class="[
      `preselect__container--${colorSchema}`,
      { 'preselect__container--wide': showFullWidth },
    ]"
    :style="containerStyle"
  >
    <div class="preselect--wrapper">
      <div v-if="title" class="preselect__title--wrapper">
        <div class="preselect__title heading-h3 heading-thick">{{ title }}</div>
      </div>
      <div class="preselect__attributes">
        <div
          v-for="field in fields"
          :key="field.name"
          class="preselect__row"
          :class="[
            `preselect__row--${fields.length}-rows`,
            field.rowClass ? `preselect__row--${field.rowClass}` : '',
          ]"
        >
          <label :for="fieldId(field.name)" class="preselect__label">{{ field.label }}</label>
          <div v-if="loading">
            <span class="preselect__spinner" aria-hidden="true" />
          </div>
          <span v-else class="plain-selector">
            <select
              :id="fieldId(field.name)"
              :name="field.name"
              class="form__input form__input--choice"
              :value="selected[field.name] ?? ''"
              @change="onSelectChange(field.name, $event)"
            >
              <option v-if="field.placeholder" value="">{{ field.placeholder }}</option>
              <option
                v-for="option in fieldOptions(field)"
                :key="`${field.name}-${optionValue(option)}`"
                :value="optionValue(option)"
              >
                {{ optionLabel(option) }}
              </option>
            </select>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, onMounted, reactive, ref } from 'vue';

import { generateUrl } from '@shop/utils/Routing';

import type {
  CategoryPreselectBlockProps,
  PreselectFieldConfig,
  PreselectOption,
} from './CategoryPreselectBlock.types';

const props = defineProps<CategoryPreselectBlockProps>();

const toBool = (value: boolean | string | undefined): boolean => value === true || value === 'true';

const loading = ref(false);
const selected = reactive<Record<string, string>>({});
const categoryUrl = ref(props.categoryUri ?? '');

const containerStyle = computed(() => {
  const style: Record<string, string> = {};
  if (props.bgColor) {
    style.background = props.bgColor;
  }
  if (props.textColor) {
    style.color = props.textColor;
  }
  return style;
});

const fieldId = (name: string): string => `category-preselect-${name}`;

const fieldOptions = (field: PreselectFieldConfig): PreselectOption[] => field.options ?? [];

const optionValue = (option: PreselectOption): string => {
  if (typeof option === 'object' && option !== null) {
    if ('slug' in option) {
      return option.slug;
    }
    if ('id' in option) {
      return String(option.id);
    }
  }
  return String(option);
};

const optionLabel = (option: PreselectOption): string => {
  if (typeof option === 'object' && option !== null) {
    if ('label' in option) {
      return option.label;
    }
    if ('value' in option) {
      return option.value;
    }
  }
  return String(option);
};

const applyFacet = (key: string, value: string): void => {
  if (toBool(props.emitEvent)) {
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    window.location.href = url.toString();
    return;
  }

  const baseUrl = categoryUrl.value;
  if (!baseUrl) {
    return;
  }

  loading.value = true;
  window.location.href = `${baseUrl}?${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

const onSelectChange = (name: string, event: Event): void => {
  const target = event.target as HTMLSelectElement;
  selected[name] = target.value;
  applyFacet(name, target.value);
};

const loadCategory = async (): Promise<void> => {
  if (categoryUrl.value || !props.categoryId) {
    return;
  }

  loading.value = true;
  try {
    const response = await axios.get<{ uri?: string }>(
      generateUrl('get_category', { category: props.categoryId })
    );
    categoryUrl.value = response.data.uri ?? '';
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  const url = new URL(window.location.href);
  props.fields.forEach(field => {
    const value = url.searchParams.get(field.name);
    if (value) {
      selected[field.name] = value;
    }
  });

  if (!props.categoryUri && props.categoryId) {
    void loadCategory();
  }
});
</script>
